import { api } from '@ppwr/convex/_generated/api'
import { useQuery } from 'convex/react'

import type { ReadArticle, ReadFeed } from './types'

/**
 * Highest feed version this build understands. The Convex query bumps its
 * version on incompatible changes; an older build shows an empty tab rather
 * than misreading the payload.
 */
const SUPPORTED_VERSION = 1

/**
 * Marketing's `showInApp` switch is honoured here, and `order` wins over recency
 * so Lauren can pin an article to the top from the CMS without an app release.
 */
export const selectVisibleArticles = (feed: ReadFeed): ReadArticle[] =>
  feed.items
    .filter((article) => article.showInApp)
    .sort((a, b) => {
      const orderA = a.order ?? Number.MAX_SAFE_INTEGER
      const orderB = b.order ?? Number.MAX_SAFE_INTEGER
      if (orderA !== orderB) {
        return orderA - orderB
      }
      return Date.parse(b.publishedAt) - Date.parse(a.publishedAt)
    })

/** Article fields every Read analytics event carries, so events join on `article_id`. */
export const readArticleEventProps = (article: ReadArticle) => ({
  article_id: article.id,
  article_slug: article.slug,
  article_title: article.title,
  article_category: article.categories?.[0] ?? null,
  read_time_minutes: article.readTimeMinutes ?? null
})

/** The chrome-free template if the web team has built it, the public page if not. */
export const articleReaderUrl = (article: ReadArticle): string => article.appUrl ?? article.url

/**
 * The Webflow blog, mirrored into Convex by a cron (`webflow.actions.syncBlogPosts`)
 * so the API token stays server-side. The subscription is live: a sync that
 * changes a post pushes it to open clients without a refresh.
 *
 * `data` is undefined while the first result is loading.
 */
export const useReadFeed = (): { data: ReadFeed | undefined; isLoading: boolean } => {
  const feed = useQuery(api.webflow.queries.listArticles)
  if (feed === undefined) {
    return { data: undefined, isLoading: true }
  }
  return {
    data: feed.version === SUPPORTED_VERSION ? feed : { ...feed, items: [] },
    isLoading: false
  }
}
